/**
 * ATELIER component kit — the designer portal's shared page primitives.
 *
 * One identity with the vasstr.com landing: Fraunces display serif, warm bone
 * canvas, ink text, scarce orange (#d45614). Read ATELIER.md in this folder
 * for the full interaction standard before styling any page.
 */
import React from 'react';
import { motion } from 'framer-motion';

export const ORANGE = '#D45614';
export const ORANGE_DARK = '#a8430e';
export const INK = '#16130F';
export const BONE = '#FAF8F4';
export const LINE = '#EBE5DC';
export const MUTED = '#6E665C';

/**
 * PageHeader — the editorial header every page starts with.
 * Big serif title, one-line purpose, ONE primary action on the right.
 */
export const PageHeader = ({ eyebrow, title, subtitle, action, className = '' }) => (
  <div className={`mb-6 sm:mb-8 ${className}`}>
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div className="min-w-0">
        {eyebrow && (
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-primary mb-2">{eyebrow}</p>
        )}
        <h1 className="font-fell text-3xl sm:text-4xl font-semibold text-ink leading-[1.05]">{title}</h1>
        {subtitle && <p className="mt-2 text-[15px] text-muted max-w-xl">{subtitle}</p>}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  </div>
);

/** Primary pill button — THE orange. Use once per screen. */
export const PrimaryButton = ({ children, onClick, type = 'button', disabled, className = '' }) => (
  <motion.button
    type={type}
    onClick={onClick}
    disabled={disabled}
    whileHover={disabled ? {} : { scale: 1.02 }}
    whileTap={disabled ? {} : { scale: 0.98 }}
    className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    style={{ background: `linear-gradient(180deg, #e6783b, ${ORANGE})`, boxShadow: `0 12px 26px -10px ${ORANGE}80` }}
  >
    {children}
  </motion.button>
);

/** Quiet secondary button — white pill with a warm border. */
export const SecondaryButton = ({ children, onClick, type = 'button', disabled, className = '' }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-[14px] font-semibold text-ink bg-white border border-line hover:border-gray-300 transition-colors disabled:opacity-50 ${className}`}
  >
    {children}
  </button>
);

/** Content card — white on the bone canvas, warm border, soft shadow. */
export const AtelierCard = ({ children, className = '', padded = true }) => (
  <div className={`bg-white rounded-2xl border border-line shadow-[0_20px_44px_-36px_rgba(22,19,15,0.4)] ${padded ? 'p-5 sm:p-6' : ''} ${className}`}>
    {children}
  </div>
);

/** Hero stat — the number is editorial serif, like a magazine figure. */
export const StatCard = ({ label, value, sub, icon, className = '' }) => (
  <AtelierCard className={className}>
    <div className="flex items-start justify-between gap-3">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400">{label}</p>
      {icon && (
        <span className="inline-grid place-items-center w-9 h-9 rounded-xl flex-shrink-0" style={{ background: `${ORANGE}14`, color: ORANGE }}>
          {icon}
        </span>
      )}
    </div>
    <p className="mt-2 font-fell text-3xl sm:text-[34px] font-semibold text-ink tabular-nums leading-none">{value}</p>
    {sub && <p className="mt-2 text-[12.5px] text-muted">{sub}</p>}
  </AtelierCard>
);

/** Empty state — says what belongs here + ONE way to create it. */
export const EmptyState = ({ icon, title, body, action, className = '' }) => (
  <div className={`text-center py-14 px-6 ${className}`}>
    {icon && (
      <span className="inline-grid place-items-center w-16 h-16 rounded-full mb-5" style={{ background: '#F4F0E9', color: ORANGE }}>
        {icon}
      </span>
    )}
    <h3 className="font-fell text-2xl font-semibold text-ink">{title}</h3>
    {body && <p className="mt-2 text-[14.5px] text-muted max-w-sm mx-auto">{body}</p>}
    {action && <div className="mt-6 flex justify-center">{action}</div>}
  </div>
);

/** Status pill — the ONLY badge style (matches Coupons/Plugin Store). */
export const StatusPill = ({ tone = 'gray', children }) => {
  const tones = {
    green: 'bg-green-100 text-green-700',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    blue: 'bg-blue-100 text-blue-700',
    orange: 'bg-orange-100 text-[#a8430e]',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11.5px] font-bold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
};

/** Section label inside a card — small caps, warm. */
export const SectionLabel = ({ children, className = '' }) => (
  <p className={`text-[11px] font-bold uppercase tracking-[0.16em] text-gray-400 ${className}`}>{children}</p>
);
